import {useTranslation} from 'react-i18next';
import {Address, Profile, User} from '../../../types/api';
import avatar from '../../../assets/images/sample-avatar.webp';
import logo from '../../../assets/images/sample-logo.png';
import socials from '../../../lib/socials';
import {useMemo} from 'react';
import {TFunction} from 'i18next';

type Options = {
    allSocials?: boolean;
};

export function useSampleProfile(options: Options = {}): Profile {
    const {t} = useTranslation();

    return useMemo(() => createSampleProfile(t, options), [t, options]);
}

export function createSampleProfile(
    t: TFunction,
    options: Options = {},
): Profile {
    const {allSocials} = options;

    const kompanyName = t(
        'admin:signature.preview.sample.kompanyName',
        'Acme Inc',
    );
    const sampleId = 'sample';
    const sampleUrl = 'https://vkard.io';
    const firstName = t('admin:signature.preview.sample.firstName', 'Jane');
    const lastName = t('admin:signature.preview.sample.lastName', 'Doe');

    const finalSocials: string[] = allSocials
        ? Object.keys(socials)
        : ['facebook', 'linkedin', 'x'];

    const socialLinks = finalSocials.map(t => ({
        type: t,
        value: sampleUrl,
    }));

    return {
        id: sampleId,
        addresses: [
            {
                address: t(
                    'admin:signature.preview.sample.profile_address.address',
                    '5678 Second Ave',
                ),
                city: t(
                    'admin:signature.preview.sample.profile_address.city',
                    'Town',
                ),
                zipCode: t(
                    'admin:signature.preview.sample.profile_address.zip_code',
                    '67890',
                ),
                country: t(
                    'admin:signature.preview.sample.profile_address.country',
                    'Country',
                ),
            },
        ],
        emails: [
            {
                value: t(
                    'admin:signature.preview.sample.email',
                    'jane.doe@mail.com',
                ),
            },
        ],
        avatar: {
            id: sampleId,
            url: avatar,
        },
        phones: [
            {
                vcfTypes: ['work'],
                value: t(
                    'admin:signature.preview.sample.phone',
                    '+33424242424',
                ),
            },
            {
                vcfTypes: ['home'],
                value: t(
                    'admin:signature.preview.sample.phone2',
                    '+33434343434',
                ),
            },
        ],
        socials: socialLinks,
        permissions: {
            edit: true,
            acl: true,
            changeHolder: true,
            changeKompany: true,
            delete: true,
            duplicate: true,
        },
        kompany: {
            id: sampleId,
            organizationId: sampleId,
            addresses: [
                {
                    id: sampleId,
                    address: t(
                        'admin:signature.preview.sample.address.address',
                        '1234 Main St',
                    ),
                    city: t(
                        'admin:signature.preview.sample.address.city',
                        'City',
                    ),
                    country: t(
                        'admin:signature.preview.sample.address.country',
                        'Country',
                    ),
                    state: t(
                        'admin:signature.preview.sample.address.state',
                        'State',
                    ),
                    zipCode: t(
                        'admin:signature.preview.sample.address.zip_code',
                        '12345',
                    ),
                } as Address,
            ],
            displayName: kompanyName,
            locale: 'en',
            siteUrl: t(
                'admin:signature.preview.sample.website',
                'https://acme.com',
            ),
            logo: {
                id: sampleId,
                thumbUrl: logo,
            },
            emails: [
                {
                    value: t(
                        'admin:signature.preview.sample.kompany_email',
                        'office@acme.com',
                    ),
                },
                {
                    value: t(
                        'admin:signature.preview.sample.kompany_email2',
                        'guests@acme.com',
                    ),
                },
            ],
            phones: [
                {
                    vcfTypes: ['office'],
                    value: t(
                        'admin:signature.preview.sample.kompany_phone',
                        '+33929292929',
                    ),
                },
                {
                    vcfTypes: ['guest'],
                    value: t(
                        'admin:signature.preview.sample.kompany_phone2',
                        '+33939393939',
                    ),
                },
            ],
            socials: socialLinks,
            links: [],
            permissions: {
                edit: true,
                acl: true,
                delete: true,
                createProfile: true,
            },
            owner: {
                id: sampleId,
                email: '',
            } as unknown as User,
            tags: [],
        },
        firstName,
        lastName,
        title: t('admin:signature.preview.sample.title', 'Junior Sales'),
        department: t(
            'admin:signature.preview.sample.department',
            'Customer Relations',
        ),
        shareUrl: sampleUrl,
        appClipEnabled: false,
        enabled: true,
        manifestUrl: sampleUrl,
        vcfUrl: sampleUrl,
        profileLabel: `${firstName} ${lastName}`,
        profileShortLabel: firstName,
        resolvedEnabled: true,
        organizationId: sampleId,
        owner: {
            id: sampleId,
            email: '',
        } as unknown as User,
        locale: navigator.language,
        options: {},
        links: [],
        tags: [],
    } as Profile;
}
